/**
 * SEVEN SIGN-IN STATES, AND WHY "SIGNED OUT" IS NOT ONE OF THEM.
 * ============================================================
 *
 * `src/core/api.ts` is organised around one distinction — `not-built-yet` is not `offline` is not
 * `network-error` — and `src/compose/ApiNotice.tsx` is where that distinction either survives to the
 * screen or dies. This file is the same idea for sign-in, because the failure mode is identical: a
 * single "signed out, please sign in again" screen is a lie in four of the seven cases below.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE CASE THIS FILE WAS WRITTEN FOR: **07:00 sign-in, 18:00 return to signal.**
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * The platform fixes the access token at **15 minutes** and the refresh token at **10 hours** (the
 * Fresnel/PRISM Authentication HLD; not values this app chooses). Sign in at 07:00, work a site with no
 * coverage, come back into signal at 18:00 — that is **eleven hours, and the refresh token is dead.**
 * There is no way to renew it and no way to have avoided it: authentication needs a network and the
 * technician did not have one.
 *
 * **This is an ordinary, expected outcome, and it is the single most likely real-world auth failure in
 * this app.** So it gets its own state — `session-expired` with reason `offline-too-long` — and its own
 * words, and the first thing those words say is that **nothing was lost**. A technician who has just
 * finished a day's work and is shown a bare sign-in screen will assume the day is gone. It is not: the
 * submissions are in IndexedDB, the queue drains the moment a token exists, and they will not retype a
 * single field.
 *
 * ⚠️ **NOT ONE STATE HERE TOUCHES THE OUTBOX.** There is no import of `outbox.ts` anywhere in
 * `src/auth/`. `src/core/sync.ts` treats 401/403 as "release the claim **without** consuming an
 * attempt", specifically so a sign-in problem cannot exhaust a work queue, and every body of copy below
 * says out loud that the saved work is still on the phone. A sign-out that cleared queued submissions
 * would be the most destructive bug this app could have.
 *
 * **THE PWA NEVER HANDLES A CREDENTIAL.** Technicians self-register on the IdPrism portal with a
 * password and a device-bound FIDO2 passkey (biometric- or PIN-protected). This app redirects, and
 * receives a code back. There is no password field, no passkey handling and no username input anywhere
 * in `src/auth/`, and `canStartSignIn` below means "a redirect can usefully be started", never "show a
 * login form".
 */

import { PLATFORM_REFRESH_TOKEN_TTL_MS } from './config';

export type AuthPhase =
  /** No sign-in has been attempted in this tab. */
  | { readonly kind: 'never-signed-in' }
  /** A redirect is about to happen, or a code is being exchanged. */
  | { readonly kind: 'signing-in' }
  | {
      readonly kind: 'signed-in';
      /** Epoch ms of the access token's expiry. */
      readonly expiresAt: number;
      /** Epoch ms the session began — the clock the 10-hour refresh window runs on. */
      readonly signedInAt: number;
      /** `sub`, for diagnostics only. Never presented as verified identity — see `jwt.ts`. */
      readonly subject: string | null;
    }
  /**
   * Expired, renewable, **NOT signed out**. The refresh token is still held, so reconnecting fixes this
   * with no typing. `cause` separates "this phone has no network" from "this phone has a network but the
   * sign-in service did not answer", which are different things to report.
   */
  | {
      readonly kind: 'session-stale';
      readonly expiredAt: number;
      readonly cause: 'offline' | 'idp-unreachable';
    }
  /**
   * The session is over and credentials are needed again. **Nothing queued is affected.**
   *
   *  · `offline-too-long`  — the 10-hour refresh window elapsed while there was no signal. Expected.
   *  · `refused`           — the IdP rejected the refresh token for some other reason (the account was
   *                          disabled, the app client changed). Rarer, and worth reporting.
   *  · `no-refresh-token`  — the access token expired and no refresh token was ever issued, so the
   *                          session could never have been renewed. A configuration finding, not a
   *                          technician problem.
   */
  | {
      readonly kind: 'session-expired';
      readonly reason: 'offline-too-long' | 'refused' | 'no-refresh-token';
      readonly detail: string;
      /** Epoch ms the dead session began, when known. Used only for wording. */
      readonly signedInAt: number | null;
    }
  /** An attempt was made and did not complete: mismatched `state`, an IdP `error`, a refused exchange. */
  | { readonly kind: 'sign-in-failed'; readonly code: string; readonly detail: string }
  /** No sign-in configuration in this build. Nothing was sent and nothing can be. */
  | { readonly kind: 'not-configured'; readonly detail: string };

/** Reuses the four tones the app's CSS already defines, so no new class names are introduced. */
export type AuthTone = 'ok' | 'offline' | 'info' | 'danger';

export interface AuthNotice {
  readonly tone: AuthTone;
  readonly title: string;
  readonly body: string;
  /**
   * Whether starting a sign-in **redirect** can usefully be offered. Never means "show a login form" —
   * this app has none.
   */
  readonly canStartSignIn: boolean;
  /** A string for support to quote. Never a token, never a stack. */
  readonly detail?: string;
}

/**
 * The pre-travel warning, and it belongs on screen while things are still fine.
 *
 * `src/core/api.ts`'s `RecoveryVerdict` sets the precedent: the warning a technician needs is the one
 * they get **before** they drive somewhere with no signal, not the explanation afterwards. A sign-in
 * lasts ten hours and cannot be renewed without a network, so "sign in before you set off" is
 * actionable and "your session expired" is not.
 */
const OFFLINE_READINESS =
  'One more thing worth knowing: a sign-in lasts about 10 hours and can only be renewed where there is ' +
  'signal. If you are heading somewhere with no coverage for a long day, sign in before you set off — ' +
  'and if it runs out while you are out there, nothing is lost, you just sign in again when you are back.';

/**
 * One phase, one set of words. Exhaustive with a `never` guard, so a new phase must be given its own
 * wording rather than silently inheriting somebody else's — the mechanism `ApiNotice.tsx` uses, for the
 * same reason.
 */
export function describeAuthPhase(phase: AuthPhase, online: boolean): AuthNotice {
  switch (phase.kind) {
    case 'signed-in':
      return {
        tone: 'ok',
        title: 'Signed in',
        body: `Submissions will send as soon as they are finished. ${OFFLINE_READINESS}`,
        canStartSignIn: false,
      };

    case 'signing-in':
      return {
        tone: 'info',
        title: 'Signing in…',
        body: 'Finishing sign-in. This needs a network for a few seconds.',
        canStartSignIn: false,
      };

    case 'never-signed-in':
      return online
        ? {
            tone: 'info',
            title: 'You are not signed in yet',
            body:
              'You can fill in and save work without signing in — it is kept on this phone. Signing in is ' +
              `what lets the app send it. ${OFFLINE_READINESS}`,
            canStartSignIn: true,
          }
        : {
            /*
             * The one case where the honest answer is "you cannot do this right now". First sign-in is a
             * redirect to a login page plus a passkey prompt, both of which need a network — the HLD is
             * explicit that there is no offline authentication by design. Offering a button here would
             * produce a blank browser tab in a locker bay.
             */
            tone: 'offline',
            title: 'You are not signed in, and signing in needs a connection',
            body:
              'Keep working — everything you fill in is saved on this phone. When you next have signal, ' +
              'sign in and it will all send. You cannot sign in for the first time without a network: the ' +
              'sign-in page and your passkey check both happen over the internet.',
            canStartSignIn: false,
          };

    /** Recoverable with no user action at all. Note the title does not contain the words "signed out". */
    case 'session-stale':
      return {
        tone: 'offline',
        title: 'Your sign-in needs renewing — nothing has been lost',
        body:
          phase.cause === 'offline'
            ? 'You are still signed in, but the app needs a moment of signal to renew it. You have not ' +
              'been signed out and nothing you typed has been lost. Keep working: your saved work stays in ' +
              'the outbox and sends by itself once you have a connection. You will not have to type ' +
              'anything again.'
            : 'The phone has a network but the sign-in service did not answer, so the app could not renew ' +
              'your sign-in. You have not been signed out and nothing has been lost. It will keep trying by ' +
              'itself; your saved work stays in the outbox until it succeeds.',
        canStartSignIn: phase.cause === 'idp-unreachable',
        detail: `stale since ${new Date(phase.expiredAt).toISOString()} · ${phase.cause}`,
      };

    /**
     * THE 07:00-to-18:00 CASE. Read the first sentence: it is about the work, not about the session.
     *
     * That ordering is the whole point. A technician reading this has just come back into signal after a
     * long day and the question in their head is "did I lose it?" — so the answer goes first, before any
     * explanation of what expired and before any instruction.
     */
    case 'session-expired':
      return {
        tone: 'danger',
        title: 'Your session expired while you were offline — your work is saved',
        body:
          phase.reason === 'offline-too-long'
            ? 'Nothing has been lost. Everything you submitted today is still saved on this phone, exactly ' +
              'as you left it. A sign-in only lasts about 10 hours and cannot be renewed without signal, ' +
              'so a long day out of coverage runs it out — that is normal and it is not a fault. Sign in ' +
              'again and everything waiting will send by itself. You will not have to retype anything.'
            : phase.reason === 'refused'
              ? 'Nothing has been lost — everything you submitted is still saved on this phone. The sign-in ' +
                'service would not renew your session, which usually means the account was changed at our ' +
                'end rather than anything you did. Sign in again and your saved work will send by itself. ' +
                'If signing in does not work, report the code below.'
              : 'Nothing has been lost — everything you submitted is still saved on this phone. This ' +
                'sign-in was issued without the means to renew itself, which is a setup problem on our ' +
                'side and worth reporting. Sign in again and your saved work will send by itself.',
        canStartSignIn: online,
        detail:
          `${phase.reason} · ${phase.detail}` +
          (phase.signedInAt === null
            ? ''
            : ` · signed in ${new Date(phase.signedInAt).toISOString()} (window ${
                PLATFORM_REFRESH_TOKEN_TTL_MS / 3_600_000
              }h)`),
      };

    case 'sign-in-failed':
      return {
        tone: 'danger',
        title: 'Sign-in did not finish',
        body:
          'The sign-in attempt was stopped before it completed, so you are not signed in. Nothing you had ' +
          'saved is affected — it is still on this phone. Try again; if it keeps failing, report the code ' +
          'below. This is not the same as a session running out: nothing expired, the attempt itself did ' +
          'not go through.',
        canStartSignIn: online,
        detail: `${phase.code}${phase.detail.length > 0 ? ` · ${phase.detail}` : ''}`,
      };

    /**
     * Distinct from every signed-out variant above, for the reason `api.ts` keeps `auth-not-configured`
     * apart from `unauthenticated`: **nothing was sent and nothing could be.** There is no session to
     * have expired and no credential to have got wrong.
     */
    case 'not-configured':
      return {
        tone: 'info',
        title: 'Sign-in is not set up in this build',
        body:
          'This copy of the app has no sign-in configuration, so it is not sending anything to the server. ' +
          'That is not a signed-out state and it is not a connection problem — it is missing setup. ' +
          'Everything you submit is still saved on this phone.',
        canStartSignIn: false,
        detail: phase.detail,
      };

    default: {
      const unreachable: never = phase;
      return unreachable;
    }
  }
}

/* ----------------------------------------------------------- the pre-travel runway ---- */

/**
 * How close to the end of the 10-hour window the Jobs screen starts saying so. **Two hours.**
 *
 * Why two: the decision this warning informs is "do I sign in again before I drive?", and the unit of
 * exposure is one job — travel plus the work itself routinely exceeds two hours, so a technician picking
 * a job with less runway than that can plausibly outrun the session *on this job*, which is exactly when
 * the warning is worth a banner. Any earlier and it is on screen most of a shift, and a standing banner
 * is a banner nobody reads — the general "sign in before you set off" advice already lives in
 * {@link OFFLINE_READINESS} on the Device screen, permanently, where permanence is the point.
 */
export const SESSION_RUNWAY_WARNING_MS = 2 * 60 * 60 * 1000;

/** A compact, conditional variant of {@link OFFLINE_READINESS} — same facts, shown only when they bite. */
export interface SessionRunwayWarning {
  readonly tone: AuthTone;
  readonly title: string;
  readonly body: string;
}

/** The clock's words. Exported only for tests; the shape ("about … left") is part of the wording. */
export function runwayWords(remainingMs: number): string {
  if (remainingMs <= 0) return 'almost none of it left';
  const minutes = Math.round(remainingMs / 60_000);
  if (minutes < 60) return `about ${minutes} minute${minutes === 1 ? '' : 's'} left`;
  const hours = Math.round(minutes / 30) / 2; // nearest half hour — "about 1.5 hours left"
  return `about ${hours} hour${hours === 1 ? '' : 's'} left`;
}

/**
 * The 10-hour warning at the moment it is decision-relevant, or `null` — which is most of the time.
 *
 * {@link describeAuthPhase}'s `signed-in` copy carries {@link OFFLINE_READINESS} permanently on the
 * Device screen. This is the same fact surfaced where the decision is made — the Jobs screen, before
 * travel — and it is deliberately conditional: `null` unless the phase is `signed-in` AND the remaining
 * refresh window is inside {@link SESSION_RUNWAY_WARNING_MS}. Every other phase already has its own
 * words (a stale or expired session is not "close to" anything, it has happened, and `SignInBanner`
 * says so), so this function refuses to compete with them.
 *
 * Pure on purpose, `now` a parameter: the caller supplies the clock, a test supplies any clock.
 */
export function describeSessionRunway(phase: AuthPhase, now: number): SessionRunwayWarning | null {
  if (phase.kind !== 'signed-in') return null;
  const remaining = phase.signedInAt + PLATFORM_REFRESH_TOKEN_TTL_MS - now;
  if (remaining > SESSION_RUNWAY_WARNING_MS) return null;
  return {
    tone: 'info',
    title: 'Your sign-in is close to its 10-hour limit',
    body:
      `This sign-in has ${runwayWords(remaining)} and can only be renewed where there is signal. ` +
      'If the next job is somewhere with no coverage, sign in again before you set off. If it runs ' +
      'out anyway, nothing is lost — your saved work stays on this phone and sends after you sign ' +
      'in again.',
  };
}

/** True when the app holds a token it believes is currently usable. */
export function isSignedIn(phase: AuthPhase): boolean {
  return phase.kind === 'signed-in';
}

/**
 * True when the phase recovers **without the technician doing anything** — the app only needs a network.
 * `sync.ts` keeps items queued in exactly these cases, and so does every other case; the difference is
 * only whether a human has to act.
 */
export function isRecoverableWithoutUser(phase: AuthPhase): boolean {
  return phase.kind === 'session-stale' || phase.kind === 'signing-in';
}

/**
 * True when queued work is untouched by this phase. **Constant `true` — and that is the point.**
 *
 * This is not a flag anything sets; it is a statement that no sign-in state has ever been a reason to
 * discard work, written as a function so a test can assert it across every variant of {@link AuthPhase}
 * and so anyone tempted to add a destructive phase has to change this line and explain themselves.
 */
export function queuedWorkSurvives(_phase: AuthPhase): true {
  return true;
}
